import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { type ConversionStep } from '@etiquetachef/types';
import { formatInt } from '@/features/dashboard-finance/formatters';

export type FunnelChartProps = {
  /** Título do card — a superfície a que este funil se refere. */
  title: string;
  /** Etapas na ordem, específicas da superfície (Android e web diferem). */
  order: readonly ConversionStep[];
  /** Contagem por etapa. Etapa ausente conta como zero. */
  steps: Partial<Record<ConversionStep, number>>;
};

/**
 * Gradiente azul → verde → âmbar ao longo do funil. Tem que cobrir 13
 * etapas; a lista anterior tinha nove e a décima em diante caía no cinza
 * de fallback, o que fazia o fim do funil parecer desabilitado.
 */
const STEP_COLORS = [
  '#1d4ed8',
  '#1e56d6',
  '#2563eb',
  '#3b82f6',
  '#0ea5e9',
  '#06b6d4',
  '#0d9488',
  '#10b981',
  '#22c55e',
  '#65a30d',
  '#84cc16',
  '#eab308',
  '#facc15',
];

/**
 * Funil horizontal custom (sem equivalente nativo no Cloudscape — Sprint 39).
 * Uma barra por etapa, com a conversão contra o topo e contra a anterior.
 */
export function FunnelChart({ title, order, steps }: FunnelChartProps): React.JSX.Element {
  const { t } = useTranslation();
  const topStep = order[0];
  const top = topStep === undefined ? 0 : (steps[topStep] ?? 0);
  const max = Math.max(top, 1);

  return (
    <Container header={<Header variant="h3" description={t('funnel.chart.hint')}>{title}</Header>}>
      <SpaceBetween size="xs">
        {order.map((step, i) => {
          const value = steps[step] ?? 0;
          const widthPct = (value / max) * 100;
          const conv = top === 0 ? 0 : value / top;
          const prevStep = i === 0 ? null : order[i - 1];
          const prevValue = prevStep === null || prevStep === undefined ? value : (steps[prevStep] ?? 0);
          const stepConv = prevValue === 0 ? 0 : value / prevValue;
          const color = STEP_COLORS[i] ?? '#6b7280';
          return (
            // Linha NÃO interativa de propósito: `listDrilldown` não
            // implementa a métrica `funnelStep` (lança `unimplemented`), então
            // a barra clicável abria um modal com erro vermelho toda vez.
            // Prometer uma interação que sempre falha é pior que não prometer.
            <div
              key={step}
              aria-label={t(`funnel.steps.${step}`)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                height: 36,
                borderRadius: 6,
              }}
            >
              {/* Label sempre legível: texto escuro sobre o fundo do container,
                  fora da barra colorida (que pode ter largura 0 quando value=0). */}
              <span
                style={{
                  flex: '0 0 180px',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#16191f',
                }}
              >
                {t(`funnel.steps.${step}`)}
              </span>
              {/* Trilho com preenchimento proporcional — nunca branco. */}
              <div
                style={{
                  position: 'relative',
                  flex: 1,
                  minWidth: 40,
                  height: '100%',
                  backgroundColor: '#eef1f5',
                  borderRadius: 6,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: `${widthPct.toFixed(2)}%`,
                    backgroundColor: color,
                    borderRadius: 6,
                  }}
                />
              </div>
              <span
                style={{
                  flex: '0 0 auto',
                  whiteSpace: 'nowrap',
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#16191f',
                }}
              >
                {formatInt(value)} ({(conv * 100).toFixed(1)}% · ↓{(stepConv * 100).toFixed(1)}%)
              </span>
            </div>
          );
        })}
      </SpaceBetween>
    </Container>
  );
}
